import _ from "underscore";
import { Dialog } from "./dialog";
import { dstCalculator, getPreviousDayStart, getStartOfDayUserTimezone } from "../../utils/date";
import Translator from "../../utils/translator";
import { getServerDate } from "../../utils/storage/date";
import template from "./shortenOrRemoveLogDialog.tmpl";
import dayjs from "../../utils/dayjs";
import DutyStatusLog from "../../model/DutyStatusLog";
import { EditPcYmAction, getMinMaxAllowedShorteningTimes, ShortenYmPcError, validateExemptionTimeModification } from "../hos/tabs/logs/editPcOrYmUtils";
import { hide, show } from "../../utils/vanilla";

export interface ShortenOrRemoveLogDialogOptions {
    translator: Translator;
    language?: string;
    currentLog: DutyStatusLog;
    nextLog: DutyStatusLog;
    latestValidEndDateTime: Date;
    selectCallback: (selectedDateTime: Date | null, action: EditPcYmAction) => void;
    isUserNightMode: boolean;
    title: string;
    defaultAction?: EditPcYmAction;
}
interface IDatePickerOptions {
    value: string;
    label: string;
    className?: string;
}
const VALIDATION_ERRORS = {
    [ShortenYmPcError.PreceedsStartTime]: "End time cannot precede the start time.",
    [ShortenYmPcError.ExceedsEndTime]: "End time cannot exceed the original duration.",
    [ShortenYmPcError.ExemptionTooShortToShorten]: "Exemption must be at least 2 minutes to shorten."
};
const TRANSLATIONS = [ "Must be between {fromDate} and {toDate}." ];

export default class ShortenOrRemoveLogDialog {
    private readonly selectCallback: (selectedDateTime: Date | null, action: EditPcYmAction) => void;
    private readonly translator: Translator;
    private readonly title: string | undefined;
    private readonly latestValidEndDateTime: Date;
    private readonly isUserNightMode: boolean;
    private readonly nextLog: DutyStatusLog;
    private readonly currentLog: DutyStatusLog;
    private readonly language: string;
    private readonly defaultAction: EditPcYmAction;
    private readonly isExemptionTooShortToShorten: boolean;
    private translations: IIdDictionary<string> = {};
    private dialog: Dialog | null;
    private selectedDate: string | null = null;

    constructor(options: ShortenOrRemoveLogDialogOptions) {
        this.selectCallback = options.selectCallback;
        this.title = options.title;
        this.translator = options.translator;
        this.latestValidEndDateTime = options.latestValidEndDateTime;
        this.isUserNightMode = options.isUserNightMode;
        this.currentLog = options.currentLog;
        this.nextLog = options.nextLog;
        this.language = options.language || "en";
        this.defaultAction = options.defaultAction || EditPcYmAction.Shorten;

        this.getStringsToBeTranslated();

        this.isExemptionTooShortToShorten = validateExemptionTimeModification(
            this.latestValidEndDateTime,
            this.currentLog,
            this.nextLog
        ) === ShortenYmPcError.ExemptionTooShortToShorten;
    }

    render() {
        const dateOptionsTmpl = this.formatDates();

        const translationsToFetchKeys = Object.keys(this.translations);
        this.translator
            .translate(translationsToFetchKeys.map(key => this.translations[key]), this.language)
            .then(translations => {
                translationsToFetchKeys.forEach((key, index) => this.translations[key] = translations[index]);

                let templateStr = _.template(template)({
                    dateOptionsTmpl: dateOptionsTmpl,
                    selectedHour: this.latestValidEndDateTime.getHours(),
                    selectedMinute: this.latestValidEndDateTime.getMinutes(),
                    defaultAction: this.defaultAction,
                    allowedShorteningRangeString: this.getAllowedShorteningRangeString()
                });

                this.dialog = new Dialog(
                    "shorten-remove-modal",
                    templateStr,
                    this.translator,
                    this.title,
                    [
                        [
                            {
                                id: "shorten-remove-modal__done",
                                text: "Next",
                                callback: () => this.done()
                            }
                        ]
                    ],
                    {
                        isNightMode: this.isUserNightMode
                    }
                );

                return this.dialog.open();
            })
            .then(() => {
                const el = this.dialog?.el;
                if (el) {
                    const { dateContainer, dateInput, hoursInput, minutesInput } = this.getDialogElements();
                    const actionToggle = el.querySelector("#editActionToggle") as HTMLDivElement;
                    const shortenDateTimeSelector = el.querySelector("#shorten-datetime-selector") as HTMLDivElement;
                    const isDefaultActionShorten = this.defaultAction === EditPcYmAction.Shorten;

                    // Always show date container if needed
                    dateContainer && dateOptionsTmpl.length > 1 && show(dateContainer);
                    if (this.isExemptionTooShortToShorten) {
                        this.hideDateTimeInputs();
                        isDefaultActionShorten && this.updateValidationDisplay(ShortenYmPcError.ExemptionTooShortToShorten);
                    }

                    // Set initial state based on default action
                    shortenDateTimeSelector.style.display = isDefaultActionShorten ? "block" : "none";

                    const validateOnChange = () => this.validateAndShowError();
                    actionToggle.addEventListener("change", () => {
                        const actionValue = el.querySelector(`#editActionToggle input[type="radio"]:checked`) as HTMLInputElement;
                        if (actionValue && shortenDateTimeSelector) {
                            shortenDateTimeSelector.style.display = actionValue.value === "Shorten" ? "block" : "none";
                            validateOnChange();
                        }
                    });

                    dateInput?.addEventListener("change", validateOnChange);
                    hoursInput?.addEventListener("change", validateOnChange);
                    minutesInput?.addEventListener("change", validateOnChange);
                }
            });

        return this;
    }

    formatDates() {
        const startDate = this.currentLog.getDate();
        let endDate = getStartOfDayUserTimezone(this.latestValidEndDateTime);

        // Create an array of all dates between start and end date
        const dates = [new Date(endDate)];
        while (endDate >= startDate) {
            endDate = getPreviousDayStart(endDate, true);
            dates.push(new Date(endDate));
        }

        // Convert dates to options
        const startOfDayToday = getStartOfDayUserTimezone(getServerDate());
        const startOfDayYesterday = getPreviousDayStart(startOfDayToday, true);
        const dateOptions: IDatePickerOptions[] = [];

        dates.forEach(date => {
            const dateString = dayjs(dstCalculator.toLocalDateTime(date), this.language).format("ddd MMM D");
            let label = "";
            let className = "";
            if (this.isSameDay(date, startOfDayToday)) {
                label = "Today";
            } else if (this.isSameDay(date, startOfDayYesterday)) {
                label = "Yesterday";
            } else {
                label = dateString;
                className = "noTranslate";
            }

            dateOptions.push({
                value: date.toISOString(),
                label,
                className
            });
        });

        this.selectedDate = dateOptions[0].value;

        const dateOptionsTmpl: string[] = [];
        dateOptions.forEach(option => {
            let selected = option.value === this.selectedDate ? "selected" : "";
            dateOptionsTmpl.push(`<option value="${ option.value }" class="${ option.className }" ${ selected }>${ option.label }</option>`);
        });

        return dateOptionsTmpl;
    }

    remove() {
        this.dialog && this.dialog.remove();
        return this;
    }

    private getDialogElements() {
        const dialogEl = this.dialog?.el;
        return {
            dialogEl,
            actionValue: dialogEl?.querySelector(`#editActionToggle input[type="radio"]:checked`) as HTMLInputElement,
            dateInput: dialogEl?.querySelector(".datetime__date") as HTMLSelectElement,
            hoursInput: dialogEl?.querySelector(".datetime__hours") as HTMLInputElement,
            minutesInput: dialogEl?.querySelector(".datetime__minutes") as HTMLInputElement,
            validationErrorContainer: dialogEl?.querySelector(".shortenOrRemoveValidationError") as HTMLElement,
            nextButton: dialogEl?.querySelector("#shorten-remove-modal__done") as HTMLButtonElement,
            dateContainer: dialogEl?.querySelector("#dateInputContainer") as HTMLDivElement,
            shortenContainer: dialogEl?.querySelector("#shorten-input-container") as HTMLDivElement
        };
    }

    private getSelectedDateTime() {
        const { dateInput, hoursInput, minutesInput } = this.getDialogElements();
        const date = dateInput?.value;
        const hours = hoursInput?.value;
        const minutes = minutesInput?.value;

        const dateObj = new Date(date);
        dateObj.setHours(parseInt(hours));
        dateObj.setMinutes(parseInt(minutes));

        // Convert the user's local time selection to UTC for proper validation and storage
        return dstCalculator.toReverseOffsetDateTime(dateObj);
    }

    private done() {
        const { actionValue } = this.getDialogElements();
        const action = actionValue.value === "Remove" ? EditPcYmAction.Remove : EditPcYmAction.Shorten;

        let error: ShortenYmPcError | null = null;
        let dateObj: Date | null = null;

        if (action === EditPcYmAction.Shorten) {
            dateObj = this.getSelectedDateTime();
            error = this.validate(dateObj, action);
        }

        this.updateValidationDisplay(null);

        if (!error) {
            this.dialog && this.dialog.remove();
            this.selectCallback(dateObj, action);
        } else {
            this.updateValidationDisplay(error);
        }
    }

    private validateAndShowError() {
        const { actionValue } = this.getDialogElements();

        // Only validate if the action is "Shorten"
        if (actionValue?.value !== "Shorten") {
            this.updateValidationDisplay(null);
            return;
        }

        const dateObj = this.getSelectedDateTime();
        const error = this.validate(dateObj, EditPcYmAction.Shorten);
        this.updateValidationDisplay(error);
    }

    private getAllowedShorteningRangeString() {
        const { minAllowedTime, maxAllowedTime } = getMinMaxAllowedShorteningTimes(this.currentLog, this.nextLog);
        const minLocalizedDate = dayjs(dstCalculator.toLocalDateTime(minAllowedTime));
        const maxLocalizedDate = dayjs(dstCalculator.toLocalDateTime(maxAllowedTime));
        const format = minLocalizedDate.isSame(maxLocalizedDate, "date") ? "LT" : "lll";

        return this.translations["Must be between {fromDate} and {toDate}."]
            .replace("{fromDate}", minLocalizedDate.format(format))
            .replace("{toDate}", maxLocalizedDate.format(format));
    }

    private getStringsToBeTranslated() {
        Object.keys(VALIDATION_ERRORS).forEach(key => {
            this.translations[key] = VALIDATION_ERRORS[key];
        });

        TRANSLATIONS.forEach(translation => {
            this.translations[translation] = translation;
        });
    }

    private updateValidationDisplay(error: ShortenYmPcError | null) {
        const { validationErrorContainer, nextButton } = this.getDialogElements();
        const message = error && this.translations[error] || "";

        if (message) {
            const textContainer = validationErrorContainer.querySelector(".noticeBox__content") as HTMLElement;
            textContainer.textContent = message;
            show(validationErrorContainer);
            nextButton.disabled = true;
        } else {
            hide(validationErrorContainer);
            nextButton.disabled = false;
        }
    }

    private validate(dateTime: Date, action: EditPcYmAction): ShortenYmPcError | null {
        return action === EditPcYmAction.Remove ? null : validateExemptionTimeModification(dateTime, this.currentLog, this.nextLog);
    }

    private hideDateTimeInputs(): void {
        const { shortenContainer } = this.getDialogElements();
        hide(shortenContainer);
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        return date1.getTime() === date2.getTime();
    }
}